import Promise from 'promise';

import * as utility from './utility';
import { sectionInfo} from './data/FeatureSectionInfo';
import { getProfileData } from './data/DataStore';

const NOVELIST_SELECT_ID = 'NoveListSelect';
const NOVELIST_SELECT_DATA_ATTRIBUTE = 'data-novelist-novelistselect';
const NOVELIST_SELECT_ERROR_MSG = 'Cannot find valid element to mount NoveList Select to.';

/**
 * Load a script into a javascript tag
 * @param  {string} url The url of the script to load
 */
export function loadScript(url, callback) {
	var script = document.createElement("script");
	script.type = "text/javascript";
	script.src = url;
	var head = document.getElementsByTagName('head')[0];
    head.appendChild(script);
    script.onload = callback;
}

/**
 * Load a style sheet into a link tag
 * @param  {string} theStyle The url of the style to load
 */
export function loadStyle(url) {
	var fileref = document.createElement("link");
	fileref.setAttribute("rel", "stylesheet");
	fileref.setAttribute("type", "text/css");
    fileref.setAttribute("href", url);
    document.getElementsByTagName("head")[0].appendChild(fileref);
}

/**
 * Send Request GET logging request
 * @param  {string} url The url of the logging
 */
export function sendLoggingV4Request(url) {
    fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
    })
    .catch((error) => {});
}

/**
 * Loads the assets supplied from InterfaceForProfile or ProfileSettings
 */
export function loadAssets(data, callback, baseUrl)
{
    var isAssetCss = function(asset) {
        return asset.MimeType.toLowerCase() === "text/css";
    }
	let assets = [];

	if (data.Assets) {
		assets = Object.keys(data.Assets).map((key) => {
			return new Promise((resolve, reject) => {
				var assetObj = data.Assets[key];
				if (isAssetCss(assetObj)) {
					utility.loadStyle(getUrl(assetObj.Url, baseUrl));
					// Resolving immediately because we don't care when the css is loaded
					resolve();
				} else {
					utility.loadScript(getUrl(assetObj.Url, baseUrl), resolve);
				}
			});
		});
	}
    Promise.all(assets).then(() => {
        callback();
    });
}

/**
 * checks if the URL is relative, and if not then fully qualifies it with the supplied base
 */
export function getUrl(url, baseUrl) {
    return isRelativeResource(url) ? baseUrl + url : url;
}

/**
 * Checks if the supplied URL is relative
 */
export function isRelativeResource(url) {
	if (/^\/[^/]/.test(url)) {
		return true;
	}
	return false;
}

/**
 * We are now forcing all requests back to NS to be secure
 */
export function getNovSchema() {
    return "https:";
}

/**
 * Gets the Url used to load eitInit.js, which is used by integrarions using the 1.x version of EIT-based NoveList Select
 *
 * @export
 * @returns url string
 */
export function getEITScriptSrc() {
    var eit = document.getElementById('EIT');
    return eit === null ? null : eit.getAttribute('src');
}

export function getProfileFromSrc(src){
    var matches = src.match(/profile=([^&]+)/i);
    if (matches !== null) {
        return matches[1];
    }
    else {
        return null;
    }
}

export function getPasswordFromSrc(src){
    var matches = src.match(/pwd=([^&]+)/i);
    if (matches !== null) {
        return matches[1];
    }
    else {
        return null;
    }
}

/**
 * Gets the container element for the React app to mount onto.
 *
 * First attempts to locate an element that contains the unique NoveList Select data-attribute.
 * If not found attempts to locate the container element by Id.
 *
 * @param {string} isbn The ISBN of the title currently being viewed
 * @returns The element for the application to mount onto.
 */
export function getAppMountElement(isbn) {

	const elementByDataAttribute = document.querySelector(`[${NOVELIST_SELECT_DATA_ATTRIBUTE}='${isbn}']`);
	const elementById = document.getElementById(NOVELIST_SELECT_ID);

	if (elementByDataAttribute) {
		return elementByDataAttribute;
	} else if (elementById) {
		return elementById;
	} else {
		console.log(NOVELIST_SELECT_ERROR_MSG);		//eslint-disable-line no-console
		return '';
	}
}

/**
 * Attaches a function to a callBack within the novSelect namespace
 * @param {function} functionToAttach
 * @param {string} callbackName
 */
export function attachFunctionToCallback(functionToAttach, callbackName) {
	if (!window.novSelect) window.novSelect = {};
	window.novSelect[callbackName] = functionToAttach;
}

 /** Transforms the original ContentByQuery response data into a simplified response.
 *
 * @param {object} data The ContentByQuery response data.
 * @returns {object[]} A simplified version of the ContentByQuery response data, specifically formatted for ContentForQuery requests.
 */
export function transformDataForContentForQuery(data) {
	if (!data) { return [];}

	let simplifiedResponses = [];
	const config = getProfileData();
	const clientIdentifier = data.ClientIdentifier;

	for (let feature in data.FeatureContent) {
		if (data.FeatureContent.hasOwnProperty(feature)) {
			let result = {
				client_identifier : clientIdentifier,
				feature_type : feature.toLowerCase(),
				feature_label : config.FeatureSettings[feature.toLowerCase()].displayname,
				category : data.FeatureContent[feature].category,
				category_label : lookupCategoryLabel(data.FeatureContent[feature].category)
			};

			simplifiedResponses.push(result);
		}
	}

	return simplifiedResponses;
}

/**
 * Transforms the original ContentByQuery response data into a simplified response.
 *
 * NOTE: Support for the response generated by this function is deprecated.
 *
 * @param {object} data The ContentByQuery response data.
 * @returns {object[]} A simplified version of the ContentByQuery response data, specifically formatted for ContentForIsbn requests.
 */
export function transformDataForContentForIsbn(data) {
	if (!data) { return [];}

	let simplifiedResponses = [];
	const config = getProfileData();
	const clientIdentifier = data.ClientIdentifier;

	for (let feature in data.FeatureContent) {
		if (data.FeatureContent.hasOwnProperty(feature)) {
			let result = {
				isbn : clientIdentifier,
				featureType : sectionInfo[feature.toLowerCase()].featureType,
				featureLabel : config.FeatureSettings[feature.toLowerCase()].displayname,
				category : sectionInfo[feature.toLowerCase()].category,
				categoryLabel : sectionInfo[feature.toLowerCase()].categoryLabel,
			};

			simplifiedResponses.push(result);
		}
	}

	return simplifiedResponses;
}

function lookupCategoryLabel(category) {
	let categoryLabel = "";

	switch (category) {
		case "Suggestions":
			categoryLabel = "Reading Recommendations from NoveList";
			break;
		case "BookInfo":
			categoryLabel = "Additional Book Info. from NoveList";
			break;
		case "RelatedContent":
			categoryLabel = "Related Content from NoveList";
			break;
		case "BookChat":
			categoryLabel = "BookChat from NoveList";
			break;
		default:
			break;
	}
	return categoryLabel;
}



// WEBPACK FOOTER //
// ./src/utility.js